import { call, put, takeEvery, select } from 'redux-saga/effects';
import { apiRequest } from './index';
import api from 'utils/api';
import * as userActions from 'actions/User';
import { get } from 'lodash';
import history from 'utils/history';
import { Slide, toast } from 'react-toastify';
import { t } from 'i18n';

const getUsers = apiRequest.bind(null, userActions.getUsers, api.getUsers);
const getActiveUsers = apiRequest.bind(null, userActions.getActiveUsers, api.getUsers);
const getInactiveUsers = apiRequest.bind(null, userActions.getInactiveUsers, api.getUsers);
const getActiveRentUsers = apiRequest.bind(null, userActions.getActiveRentUsers, api.getUsers);
const getInactiveRentUsers = apiRequest.bind(null, userActions.getInactiveRentUsers, api.getUsers);
const getUser = apiRequest.bind(null, userActions.getUser, api.getUser);
const createHomeUser = apiRequest.bind(null, userActions.createHomeUser, api.createHomeUser);
const createStudioUser = apiRequest.bind(null, userActions.createStudioUser, api.createStudioUser);
const createRentalUser = apiRequest.bind(null, userActions.createRentalUser, api.createRentalUser);
const createStudioUserByAdmin = apiRequest.bind(null, userActions.createStudioUserByAdmin, api.createStudioUserByAdmin);
const updateUserCredits = apiRequest.bind(null, userActions.updateUserCredits, api.updateUserCredits);
const deleteUser = apiRequest.bind(null, userActions.deleteUser, api.deleteUser);
const deleteUsers = apiRequest.bind(null, userActions.deleteUsers, api.deleteUsers);
const isBeautyCareAdmin = apiRequest.bind(null, userActions.isBeautyCareAdmin, api.isBeautyCareAdmin);

const isPayloadUserConflict = (error) => Number(get(error, 'status')) === 409;

const payloadUserConflictError = () => ({
  message: t('messages.userPayloadConflict'),
  status: 409,
  title: t('messages.operationFailed'),
  detail: t('messages.userPayloadConflict'),
});

function* updateUserRequest(entity, apiFn, action) {
  const { response, error } = yield call(apiFn, action.id, action.data);

  if (!error) {
    yield put(entity.success(response));
    return;
  }

  if (isPayloadUserConflict(error)) {
    yield put(userActions.clearUser());
    yield put(userActions.getUser.request(action.id));
    yield put(entity.failure(payloadUserConflictError()));
    return;
  }

  yield put(entity.failure(error));
}

const updateUser = updateUserRequest.bind(null, userActions.updateUser, api.updateUser);
const updateUserByAdmin = updateUserRequest.bind(null, userActions.updateUserByAdmin, api.updateUserByAdmin);

function* refreshUsers() {
  const state = yield select();
  toast(t('messages.success'), {
    transition: Slide,
    closeButton: true,
    position: 'bottom-center',
    autoClose: 2000,
    type: 'success',
  });
  const arg = {
    ...get(arguments, '0', {}),
    data: {
      page: get(state, 'User.usersPage', 0),
    },
  };

  yield apiRequest.apply(null, [userActions.getUsers, api.getUsers, arg]);
}

function* handleUpdateUser(action) {
  if (action.payload.options === 'redirect') {
    return yield history.push('/users');
  } else {
    yield history.push(`/update-user/${action.payload.id}`);
    //remove all toast messages created by api-calls/page/... before displaying success message
    yield toast.dismiss();
    yield toast(t('messages.saved'), {
      transition: Slide,
      closeButton: true,
      autoClose: 2000,
      position: 'bottom-center',
      type: 'success',
    });
  }
}

function* handleDeleteMutlipleUsers() {
  yield refreshUsers();
  yield put({ type: userActions.SELECT_USERS, payload: {} });
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

export function* watchGetUsersRequest() {
  yield takeEvery(userActions.GET_USERS.REQUEST, getUsers);
}

export function* watchGetActiveUsersRequest() {
  yield takeEvery(userActions.GET_ACTIVE_USERS.REQUEST, getActiveUsers);
}

export function* watchGetInactiveUsersRequest() {
  yield takeEvery(userActions.GET_INACTIVE_USERS.REQUEST, getInactiveUsers);
}

export function* watchGetActiveRentUsersRequest() {
  yield takeEvery(userActions.GET_ACTIVE_RENT_USERS.REQUEST, getActiveRentUsers);
}

export function* watchGetInactiveRentUsersRequest() {
  yield takeEvery(userActions.GET_INACTIVE_RENT_USERS.REQUEST, getInactiveRentUsers);
}

export function* watchGetUserRequest() {
  yield takeEvery(userActions.GET_USER.REQUEST, getUser);
}

export function* watchCreateHomeUserRequest() {
  yield takeEvery(userActions.CREATE_HOME_USER.REQUEST, createHomeUser);
}

export function* watchCreateStudioUserRequest() {
  yield takeEvery(userActions.CREATE_STUDIO_USER.REQUEST, createStudioUser);
}

export function* watchCreateRentalUserRequest() {
  yield takeEvery(userActions.CREATE_RENTAL_USER.REQUEST, createRentalUser);
}

export function* watchCreateStudioUserByAdminRequest() {
  yield takeEvery(userActions.CREATE_STUDIO_USER_BY_ADMIN.REQUEST, createStudioUserByAdmin);
}

export function* watchCreateStudioUserByAdminSuccess() {
  yield takeEvery(userActions.CREATE_STUDIO_USER_BY_ADMIN.SUCCESS, handleUpdateUser);
}

export function* watchCreateHomeUserSuccess() {
  yield takeEvery(userActions.CREATE_HOME_USER.SUCCESS, handleUpdateUser);
}

export function* watchCreateStudioUserSuccess() {
  yield takeEvery(userActions.CREATE_STUDIO_USER.SUCCESS, handleUpdateUser);
}

export function* watchCreateRentalUserSuccess() {
  yield takeEvery(userActions.CREATE_RENTAL_USER.SUCCESS, handleUpdateUser);
}

export function* watchUpdateUserRequest() {
  yield takeEvery(userActions.UPDATE_USER.REQUEST, updateUser);
}

export function* watchUpdateUserSuccess() {
  yield takeEvery(userActions.UPDATE_USER.SUCCESS, handleUpdateUser);
}

export function* watchUpdateUserCreditsRequest() {
  yield takeEvery(userActions.UPDATE_USER_CREDITS.REQUEST, updateUserCredits);
}

export function* watchUpdateUserCreditsSuccess() {
  yield takeEvery(userActions.UPDATE_USER_CREDITS.SUCCESS, handleUpdateUser);
}

export function* watchUpdateUserByAdminRequest() {
  yield takeEvery(userActions.UPDATE_USER_BY_ADMIN.REQUEST, updateUserByAdmin);
}

export function* watchUpdateUserByAdminSuccess() {
  yield takeEvery(userActions.UPDATE_USER_BY_ADMIN.SUCCESS, handleUpdateUser);
}

export function* watchDeleteUserRequest() {
  yield takeEvery(userActions.DELETE_USER.REQUEST, deleteUser);
}

export function* watchDeleteUserSuccess() {
  yield takeEvery(userActions.DELETE_USER.SUCCESS, refreshUsers);
}

export function* watchDeleteUsersRequest() {
  yield takeEvery(userActions.DELETE_USERS.REQUEST, deleteUsers);
}

export function* watchDeleteUsersSuccess() {
  yield takeEvery(userActions.DELETE_USERS.SUCCESS, handleDeleteMutlipleUsers);
}

export function* watchIsBeautyCareAdminRequest() {
  yield takeEvery(userActions.IS_BEAUTY_CARE_ADMIN.REQUEST, isBeautyCareAdmin);
}
